import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Sparkles, Globe, ArrowRight, Zap, Target, Building2 } from 'lucide-react'
import { publicAnalysisApi } from '@/services/public-api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Card, CardContent } from '@/components/ui/card'

import { isValidWebsiteInput, normalizeWebsiteUrl } from '@/lib/website-url'

export default function DemoAnalysisPage() {
  const navigate = useNavigate()
  const [website, setWebsite] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const websiteValid = isValidWebsiteInput(website)
  const canSubmit = websiteValid && !isSubmitting

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    const normalizedUrl = normalizeWebsiteUrl(website)
    if (!normalizedUrl) {
      setError('Please enter a valid website URL')
      return
    }

    setIsSubmitting(true)

    try {
      const result = await publicAnalysisApi.startAnalysis(normalizedUrl)
      navigate(`/analyze/${result.id}`)
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { status?: number; data?: { detail?: string } } }
        if (axiosError.response?.status === 429) {
          setError(axiosError.response.data?.detail || 'Rate limit exceeded. Please try again later.')
        } else {
          setError(axiosError.response?.data?.detail || 'Failed to start analysis. Please try again.')
        }
      } else {
        setError('Failed to start analysis. Please try again.')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      {/* Header */}
      <header className="border-b bg-background/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
              <Zap className="h-5 w-5 text-primary-foreground" />
            </div>
            <span className="font-semibold text-lg">Prospect Hunter</span>
          </Link>
          <Link to="/login">
            <Button variant="outline">Sign In</Button>
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <main className="container mx-auto px-4 py-16 md:py-24">
        <div className="max-w-3xl mx-auto text-center space-y-6">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium">
            <Sparkles className="h-4 w-4" />
            Free Website Analysis
          </div>

          <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Discover Your Perfect Prospects
          </h1>

          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Enter your website and we'll analyze your business to identify your ideal customers,
            suggest prospecting strategies, and recommend B2B data sources.
          </p>
        </div>

        {/* Analysis Form */}
        <div className="max-w-xl mx-auto mt-12">
          <Card className="shadow-lg">
            <CardContent className="p-6 md:p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <label
                    htmlFor="website"
                    className="text-sm font-medium text-foreground"
                  >
                    Your Website URL
                  </label>
                  <div className="relative">
                    <Globe className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                    <Input
                      id="website"
                      type="text"
                      placeholder="example.com"
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                      className={`pl-12 h-14 text-lg ${website.trim() && !websiteValid ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                      autoFocus
                      disabled={isSubmitting}
                    />
                  </div>
                  {website.trim() && !websiteValid && (
                    <p className="text-sm text-destructive">
                      Please enter a valid website (e.g., example.com)
                    </p>
                  )}
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full h-14 text-lg"
                  disabled={!canSubmit}
                >
                  {isSubmitting ? (
                    <>
                      <Sparkles className="mr-2 h-5 w-5 animate-pulse" />
                      Starting Analysis...
                    </>
                  ) : (
                    <>
                      Analyze My Website
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>

                <p className="text-center text-sm text-muted-foreground">
                  No sign-up required. Results in about 60 seconds.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Features Grid */}
        <div className="max-w-4xl mx-auto mt-20 grid md:grid-cols-3 gap-6">
          <div className="text-center space-y-3 p-6">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30">
              <Building2 className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="font-semibold text-foreground">Company Profile</h3>
            <p className="text-sm text-muted-foreground">
              We analyze your website to understand your industry, activities, and market position.
            </p>
          </div>

          <div className="text-center space-y-3 p-6">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30">
              <Target className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="font-semibold text-foreground">Ideal Customer Profile</h3>
            <p className="text-sm text-muted-foreground">
              Discover who your best customers are, their industries, sizes, and buying patterns.
            </p>
          </div>

          <div className="text-center space-y-3 p-6">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900/30">
              <Sparkles className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <h3 className="font-semibold text-foreground">Data Sources</h3>
            <p className="text-sm text-muted-foreground">
              Get personalized recommendations for B2B platforms and directories to find prospects.
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t py-8 mt-16">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} Prospect Hunter. All rights reserved.</p>
        </div>
      </footer>
    </div>
  )
}
